import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';
import { authService } from '../services/authService';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();
  
  // Hide header and footer on this page
  useEffect(() => {
    // Detect iOS for specific fixes
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS) {
      document.documentElement.classList.add('ios');
      document.body.classList.add('ios');
    }
    
    // Add classes to hide header/footer and allow scrolling
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    
    return () => {
      // Cleanup on unmount
      if (isIOS) {
        document.documentElement.classList.remove('ios');
        document.body.classList.remove('ios');
      }
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    
    // Clear error when user starts typing
    if (errors.email) {
      setErrors({});
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        // Call the actual API endpoint
        await authService.forgotPassword(email);
        
        // Show success state
        setIsSubmitted(true);
        showNotification('Password reset link sent to your email');
      } catch (error) {
        showNotification('Failed to send reset link. Please try again.', 'error');
        console.error('Password reset error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="auth-page-container" style={{ background: 'linear-gradient(180deg, #E6DFED 0%, #E7E8DD 50%, #F2DAE2 100%)' }}>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded shadow-lg z-50 ${
          notification.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {notification.message}
        </div>
      )}
      
      {/* Background decorative elements - fixed position */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[10%] left-[15%] w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-[40%] right-[15%] w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[10%] left-[35%] w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>
      
      {/* Content container */}
      <div className="mx-auto px-4 py-10 relative z-10">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            {isSubmitted ? (
              <div className="text-center space-y-4">
                <div className="rounded-full bg-green-100 p-3 w-16 h-16 flex items-center justify-center mx-auto">
                  <svg
                    viewBox="0 0 24 24"
                    width="32"
                    height="32"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-green-600"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2 className="text-xl font-medium mt-4">Check Your Email</h2>
                <p className="text-gray-500">
                  We've sent a password reset link to {email}.<br />
                  Please check your inbox and follow the instructions.
                </p>
                <button 
                  onClick={() => setIsSubmitted(false)} 
                  className="mt-4 w-full auth-button border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                >
                  Send again
                </button>
                <div className="mt-4">
                  <Link
                    to="/signin"
                    className="text-purple-600 font-medium hover:text-purple-500"
                  >
                    Return to sign in
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                    Forgot Password
                  </h1>
                  <p className="text-gray-500 mt-2">
                    Enter your email address and we'll send you a link to reset your password
                  </p>
                </div>
                
                <div className="space-y-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {errors.form && (
                      <div className="p-3 bg-red-50 text-red-700 text-sm rounded">
                        {errors.form}
                      </div>
                    )}
                    
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiMail className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          placeholder="your.email@example.com"
                          className={`auth-input ${errors.email ? 'border-red-300' : ''}`}
                          value={email}
                          onChange={handleChange}
                        />
                      </div>
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full auth-button bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white mt-4"
                      disabled={isLoading}
                    >
                      {isLoading ? "Sending..." : "Send Reset Link"}
                    </button>
                  </form>
                </div>
                
                <div className="mt-6 text-center text-sm">
                  Remember your password?{" "}
                  <Link
                    to="/signin"
                    className="text-purple-600 font-medium hover:text-purple-500"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 